import { useState } from 'react';
import { Info } from 'lucide-react';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import {
  SectionLayout,
  SectionLayoutSkeleton,
} from '@/components/ui/SectionLayout.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip.tsx';
import useGetCompanyInfo from '@/features/dashboard/company/hooks/useGetCompanyInfo.ts';
import EditCompanyNameModal from '@/features/dashboard/company/components/EditCompanyNameModal.tsx';
import EditCompanyLogoModal from '@/features/dashboard/company/components/EditCompanyLogoModal.tsx';

const CompanyInfo = () => {
  const { data: companyInfo, isLoading } = useGetCompanyInfo();
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isLogoModalOpen, setIsLogoModalOpen] = useState(false);

  if (isLoading) {
    return <SectionLayoutSkeleton rows={2} />;
  }

  if (!companyInfo) {
    return null;
  }

  const logoUrl = companyInfo.companyLogoDownloadUrl;

  return (
    <>
      <SectionLayout
        title="Dane firmy"
        description="Nazwa i logo pojawiają się w wiadomościach do uczestników, na fakturach i w panelu zapisów."
      >
        <InfoCardList>
          <InfoCardRow
            layout="columns"
            label="Nazwa firmy"
            action={
              <ActionButton
                tone="outline"
                size="toolbar"
                onClick={() => setIsNameModalOpen(true)}
              >
                Zmień
              </ActionButton>
            }
          >
            {companyInfo.companyName}
          </InfoCardRow>
          <InfoCardRow
            layout="columns"
            label={
              <>
                Logo
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="ml-1 inline text-neutral-400" />
                  </TooltipTrigger>
                  <TooltipContent>
                    Pełnowymiarowa wersja Twojego logo. Powinno mieć co najmniej
                    128px na 128px. Obsługiwane formaty to JPG i PNG.
                  </TooltipContent>
                </Tooltip>
              </>
            }
            action={
              <ActionButton
                tone="outline"
                size="toolbar"
                onClick={() => setIsLogoModalOpen(true)}
              >
                {logoUrl ? 'Zmień' : 'Dodaj'}
              </ActionButton>
            }
          >
            {/* `max-w`, nie sztywne 200 px: wiersz `columns` oddaje wartości
                tylko resztę szerokości po kolumnie etykiety. */}
            <div className="flex h-[80px] w-full max-w-[200px] items-center justify-center rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-2">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Podgląd nie dostępny"
                  className="max-h-[64px] max-w-[184px] object-contain"
                />
              ) : (
                <span className="text-[13px] font-normal text-neutral-600">
                  Brak logo
                </span>
              )}
            </div>
          </InfoCardRow>
        </InfoCardList>
      </SectionLayout>

      {isNameModalOpen && (
        <EditCompanyNameModal
          companyInfo={companyInfo}
          onClose={() => setIsNameModalOpen(false)}
        />
      )}
      {isLogoModalOpen && (
        <EditCompanyLogoModal
          companyInfo={companyInfo}
          onClose={() => setIsLogoModalOpen(false)}
        />
      )}
    </>
  );
};

export default CompanyInfo;
